import formatMessage from 'format-message'
import isEqual from 'lodash.isequal'
import { createStore } from 'redux'

import { convertStringBool } from '../utils'
import { reader as endpoints } from '../api_urls'
import {
    initializeReaderAnalyticsData,
    toggleAnalyticsUpdated,
    updatePreviousAnalyticsData,
    updateReadingAnalyticsData,
    updateVideoAnalyticsData,
    addCaliperData,
    removeCaliperData,
} from './ReaderAnalyticsActions'

import Player from '@vimeo/player'

formatMessage.setup({
    // don't console.warn or throw an error when a translation is missing
    missingTranslation: 'ignore',
})

let initialState = {
    STATUS_TEXT: [
        formatMessage('Updated'),
        formatMessage('Update Progress'),
        formatMessage('Updating...'),
        formatMessage('Update Error'),
    ],
    ANALYTICS_SYNC_RATE: 30000,
    READING_TIME_IN_SECONDS: 0.5,
    VIDEO_BLOCK_IN_SECONDS: 5,
    statusButtonID: '#booktools_progress_button',
    analyticsData: {},
    previousAnalyticsData: {},
    chapterID: null,
    pageID: null,
    pageLoadDate: null,
    analyticsUpdated: false,
}

let reducer = (state = initialState, action) => {
    switch (action.type) {
        case 'INITIALIZE_ANALYTICS_STORE':
            return Object.assign({}, state, {
                chapterID: action.chapterID,
                pageID: action.pageID,
                pageLoadDate: action.pageLoadDate,
                analyticsData: action.analyticsData,
            })
        case 'TOGGLE_ANALYTICS_UPDATED':
            return Object.assign({}, state, {
                analyticsUpdated: action.payload,
            })
        case 'UPDATE_PREVIOUS_ANALYTICS_DATA':
            return Object.assign({}, state, {
                previousAnalyticsData: $.extend(true, {}, action.payload),
            })
        case 'UPDATE_READING_ANALYTICS_DATA':
            return Object.assign({}, state, {
                analyticsData: {
                    ...state.analyticsData,
                    paras: {
                        ...state.analyticsData.paras,
                        [action.paragraphID]: action.value,
                    },
                },
            })
        case 'UPDATE_VIDEO_ANALYTICS_DATA':
            return Object.assign({}, state, {
                analyticsData: {
                    ...state.analyticsData,
                    videos: {
                        ...state.analyticsData.videos,
                        [action.fileref]: action.statsList,
                    },
                },
            })
        case 'ADD_CALIPER_DATA':
            return Object.assign({}, state, {
                analyticsData: {
                    ...state.analyticsData,
                    caliper: {
                        ...state.analyticsData.caliper,
                        [action.fileref]: action.caliperAnalytics,
                    },
                },
            })
        case 'REMOVE_CALIPER_DATA':
            return Object.assign({}, state, {
                analyticsData: {
                    ...state.analyticsData,
                    caliper: {},
                },
            })
        default:
            return state
    }
}

let analyticsStore = createStore(reducer)
window.MyEdAnalyticsStore = analyticsStore
let store = window.MyEdAnalyticsStore

/**
  Returns `true` if there are no analytics to send
*/
let isAnalyticsEmpty = () => {
    let state = store.getState()
    if ($.isEmptyObject(state.previousAnalyticsData)) {
        return false
    }

    if (isEqual(state.analyticsData, state.previousAnalyticsData)) {
        return true
    }

    return false
}

/**
  Class that handles recording and submitting reader analytics to the server
*/
export class ReaderAnalytics {
    constructor(isUserAnonymous, chapterID, pageID, pageLoadDate) {
        this.isUserAnonymous = convertStringBool(isUserAnonymous)
        this.saveAnalyticsToServerTriggered = false
        let analyticsData = {
            paras: {},
            total_paras: $('#bookcontent').find('p.para').length,
            videos: {},
            caliper: {},
        }
        store.dispatch(
            initializeReaderAnalyticsData(
                chapterID,
                pageID,
                pageLoadDate,
                analyticsData
            )
        )
    }

    /**
      Setup and start the reader analytics handlers
    */
    start() {
        this._setUpPageUnloadHandlers()
        this._setUpReadingAnalyticsHandler()
        this._attachAnalyticsSyncTimer()
        this._attachSyncAnalyticsFuncToGlobal()
        
        // vimeo analytics handlers are called immediately for any 
        // videos already on screen and in a loop for items 
        // that need to be scrolled to
        this._setUpVimeoVideoPlayerAnalyticsHandler()
        setInterval(() => {
            this._setUpVimeoVideoPlayerAnalyticsHandler()
        }, 2000)

        //due to how youtube loads its analytics API it calls its own 
        //setInterval internally
        this._setUpYouTubeVideoPlayerAnalyticsHandler()
    }

    /**
      Sync analytics to server
  
      @param syncOverride optional parameter that ensures method performs ajax call
      @param isAsync optional parameter that determines whether or not method performs asynchronous ajax call
    */
    syncAnalytics(syncOverride = false, isAsync = true) {
        let state = store.getState()
        if (
            ($(state.statusButtonID).text() === state.STATUS_TEXT[2] ||
                isAnalyticsEmpty()) &&
            !syncOverride
        ) {
            store.dispatch(toggleAnalyticsUpdated(false))
            return
        }

        if (window.updateStatusButton) {
            window.updateStatusButton(2, true)
        }

        let chapterID = state.chapterID
        let pageID = state.pageID
        let url = endpoints.recordAnalytics({ chapterID, pageID })

        $.ajax({
            url: url,
            type: 'POST',
            dataType: 'html',
            progressBarStyle: false,
            data: {
                page_load: state.pageLoadDate,
                last_page: location.pathname,
                analytics_data: JSON.stringify(state.analyticsData),
            },
            success: (data, _, response) => {
                if (response.getResponseHeader('me-response-type') === 'error') {
                    if (window.updateStatusButton) {
                        window.updateStatusButton(3, true)
                    }
                    $.meDialog.topMessage(data)
                    store.dispatch(toggleAnalyticsUpdated(false))
                } else {
                    if (window.updateStatusButton) {
                        window.updateStatusButton(0, true)
                    }
                    store.dispatch(toggleAnalyticsUpdated(true))
                    store.dispatch(updatePreviousAnalyticsData(state.analyticsData))
                }
                store.dispatch(removeCaliperData())
            },
            error: () => {
                console.error(
                    `Error updating analytics ${JSON.stringify(state.analyticsData)}`
                )
                if (window.updateStatusButton) {
                    window.updateStatusButton(3, true)
                }
                store.dispatch(toggleAnalyticsUpdated(false))
            },
            async: isAsync,
        })
    }

    /**
        Attach `syncAnalytics` func to global object
  
      This is done so `booktools.advanced_options.jsm` can call the `syncAnalytics()`
      method in the proper context.
    */
    _attachSyncAnalyticsFuncToGlobal() {
        window.MyEd_syncAnalytics = this.syncAnalytics.bind(this)
    }

    /**
      Attach analytics sync timer to DOM
    */
    _attachAnalyticsSyncTimer() {
        let state = store.getState()
        if (!this.isUserAnonymous) {
            $('#reader_navigation').powerTimer({
                sleep: state.ANALYTICS_SYNC_RATE,
                name: 'analytics_sync_timer',
                func: () => {
                    this.syncAnalytics()
                },
            })
        }
    }

    /**
      Sync analytics to server on page unload
    */
    _setUpPageUnloadHandlers() {
        $(window)
            .off('beforeunload.save_analytics')
            .on('beforeunload.save_analytics', () => {
                if (this.saveAnalyticsToServerTriggered) {
                    return
                }

                this.saveAnalyticsToServerTriggered = true
                if (!this.isUserAnonymous) {
                    this.syncAnalytics(true, true)
                }
            })

        window.addEventListener('pagehide', () => {
            $(window).trigger('beforeunload.save_analytics')
        })
    }

    /**
      Attach reading analytics handler to window
    */
    _setUpReadingAnalyticsHandler() {
        let state = store.getState()
        $(window).on(
            'scroll.reading_log_watcher resize.reading_log_watcher',
            () => {
                $('#bookcontent').powerTimer({
                    name: 'reading_log_watcher',
                    delay: state.READING_TIME_IN_SECONDS * 1000.0,
                    func: () => {
                        let now = new Date().getTime()
                        $('#bookcontent')
                            .find('p.para:onScreen')
                            .filter(':visible')
                            .each((_, para) => {
                                store.dispatch(
                                    updateReadingAnalyticsData($(para).attr('id'), now)
                                )
                                if (window.updateStatusButton) {
                                    window.updateStatusButton()
                                }
                            })
                    },
                })
            }
        )

        $(window).trigger('scroll.reading_log_watcher')
    }

    /**
     Attach Vimeo Video Player analytics handler to DOM
   */
    _setUpVimeoVideoPlayerAnalyticsHandler() {
        var helperFunctionContext = this 
        // this presevers the current 'this' when calling the helper functions for recording
        // feel free to fix if you find a better solution
        $("iframe[data-video-player='vimeo']").each(
            function() {
                let guid = $(this).attr('data-vimeo-player-guid')
                let fileref = $(this).attr('data-vimeo-id')
                let already_setup = $(this).attr('data-vimeo-handler-setup')
                if (!guid || !fileref || already_setup) {
                    return
                }
                $(this).attr('data-vimeo-handler-setup', true)

                let vimeoPlayer = new Player($(this).get(0))
                vimeoPlayer.ready().then(() => {
                    let playing = false
                    let lastData = null
                    let volume = 1
                    let muted = true
                    let count = 0 // this is hacky, but mute/unmute events trigger 2x on the first click, waiting for vimeo to fix the issue, will just ignore the second trigger for now
                    vimeoPlayer.getVolume().then(vol => {
                        volume = vol
                        if (volume > 0) {
                            muted = false
                        }
                    })

                    let lastPlayerUpdate = 0

                    vimeoPlayer.on('timeupdate', data => {
                        if (!playing) {
                            helperFunctionContext.recordVideoPlayerTime(fileref, true, 0, 0)
                            pausePlayers()
                        }
                        playing = true
                        lastData = data
                        if ($.now() - lastPlayerUpdate > 1000) {
                            helperFunctionContext.recordVideoPlayerTime(
                                fileref,
                                false,
                                data.seconds,
                                data.duration
                            )
                            lastPlayerUpdate = $.now()
                        }
                    })

                    vimeoPlayer.on('play', data => {
                        helperFunctionContext.recordCaliperAnalytics(
                            fileref,
                            'Started',
                            data.seconds,
                            data.duration,
                            Date.now()
                        )
                    })

                    vimeoPlayer.on('pause', () => {
                        playing = false
                        if (lastData) {
                            helperFunctionContext.recordVideoPlayerTime(
                                fileref,
                                false,
                                lastData.seconds,
                                lastData.duration
                            )
                            helperFunctionContext.recordCaliperAnalytics(
                                fileref,
                                'Paused',
                                lastData.seconds,
                                lastData.duration,
                                Date.now()
                            )
                        }
                    })

                    vimeoPlayer.on('ended', () => {
                        playing = false
                        if (lastData) {
                            helperFunctionContext.recordVideoPlayerTime(
                                fileref,
                                false,
                                lastData.seconds,
                                lastData.duration
                            )
                            helperFunctionContext.recordCaliperAnalytics(
                                fileref,
                                'Ended',
                                lastData.seconds,
                                lastData.duration,
                                Date.now()
                            )
                        }
                    })


                    vimeoPlayer.on('volumechange', data => {
                        count++
                        if (count != 2) {
                            // this will be removed when vimeo fixes their api
                            if (muted && data.volume > 0) {
                                muted = !muted
                                helperFunctionContext.recordCaliperAnalytics(
                                    fileref,
                                    "Unmuted",
                                    lastData.seconds,
                                    lastData.duration,
                                    Date.now(),
                                    [{ "volume": data.volume }]
                                )
                            } else if (!muted && (data.volume == 0 || data.volume == 1)) {
                                muted = !muted
                                helperFunctionContext.recordCaliperAnalytics(
                                    fileref,
                                    "Muted",
                                    lastData.seconds,
                                    lastData.duration,
                                    Date.now()
                                )
                            }
                        }
                    })

                    vimeoPlayer.on("playbackratechange", (data) => {
                        helperFunctionContext.recordCaliperAnalytics(
                            fileref,
                            "ChangedSpeed",
                            lastData.seconds,
                            lastData.duration,
                            Date.now(),
                            [{ "playbackRate": data.playbackRate }]
                        )
                    })

                    vimeoPlayer.on('seeked', data => {
                        helperFunctionContext.recordCaliperAnalytics(
                            fileref,
                            'JumpedTo',
                            data.seconds,
                            data.duration,
                            Date.now()
                        )
                    })

                    let pausePlayers = () => {
                        $('iframe[data-vimeo-player-guid]').each((index, videoPlayer) => {
                            if ($(videoPlayer).attr('data-vimeo-player-guid') !== guid) {
                                let otherPlayer = new Player(videoPlayer)
                                otherPlayer.getPaused(paused => {
                                    if (!paused) {
                                        otherPlayer.pause().then(() => { })
                                    }
                                })
                            }
                        })
                    }
                })

            })
    }

    /**
      Attach YouTube Video Player analytics handler to DOM
    */
    _setUpYouTubeVideoPlayerAnalyticsHandler() {
        // Youtube API requires that this function is available at global scope
        if ($('#youtube_api_script_tag').length === 0) {
            let tag = document.createElement('script')
            tag.src = 'https://www.youtube.com/iframe_api'
            tag.id = 'youtube_api_script_tag'
            let firstScriptTag = document.getElementsByTagName('script')[0]
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
        }

        window.onYouTubeIframeAPIReady = () => {
            setInterval(() => {

                var helperFunctionContext = this // this presevers the current 'this' when calling the helper functions for recording
                // feel free to fix if you find a better solution
                $("iframe[data-video-player='youtube']").each(
                    function() {
                        let div = $(this)
                        let divID = div.attr('id')
                        let guid = div.attr('data-youtube-player-guid')
                        let fileref = div.attr('data-youtube-id')
                        let alreadySetup = div.attr('data-analytics-handler-ready')
                        if (!guid || !fileref || alreadySetup) {
                            return
                        }
                        div.attr('data-analytics-handler-ready', true)



                        let onPlayerStateChange = () => {
                            if (player.getPlayerState() === YT.PlayerState.PLAYING) {
                                onPlayerPlay({})

                                $(this).powerTimer({
                                    name: 'youtube_player_event_timer',
                                    sleepAfter: 500,
                                    func: () => {
                                        if (player.getPlayerState() === YT.PlayerState.PLAYING) {
                                            onPlayerProgress({
                                                seconds: player.getCurrentTime(),
                                                duration: player.getDuration(),
                                            })
                                        } else {
                                            return false
                                        }
                                    },
                                })
                            } else if (player.getPlayerState() === YT.PlayerState.PAUSED) {
                                $(this).powerTimer('stop')
                                onPlayerPause({
                                    seconds: player.getCurrentTime(),
                                    duration: player.getDuration(),
                                })
                            } else if (player.getPlayerState() === YT.PlayerState.ENDED) {
                                $(this).powerTimer('stop')
                                onPlayerFinish({
                                    seconds: player.getCurrentTime(),
                                    duration: player.getDuration(),
                                })
                            } else {
                                $(this).powerTimer('stop')
                            }
                        }

                        let onPlayerPlay = data => {
                            pausePlayers()
                            helperFunctionContext.recordVideoPlayerTime(fileref, true, 0, 0)
                            helperFunctionContext.recordCaliperAnalytics(
                                fileref,
                                'Started',
                                data.seconds,
                                data.duration,
                                Date.now()
                            )
                        }

                        let onPlayerProgress = data => {
                            helperFunctionContext.recordVideoPlayerTime(
                                fileref,
                                false,
                                data.seconds,
                                data.duration
                            )
                        }

                        let onPlayerPause = data => {
                            helperFunctionContext.recordVideoPlayerTime(
                                fileref,
                                false,
                                data.seconds,
                                data.duration
                            )
                            helperFunctionContext.recordCaliperAnalytics(
                                fileref,
                                'Paused',
                                data.seconds,
                                data.duration,
                                Date.now()
                            )
                        }

                        let onPlayerFinish = data => {
                            helperFunctionContext.recordVideoPlayerTime(
                                fileref,
                                false,
                                data.seconds,
                                data.duration
                            )
                            helperFunctionContext.recordCaliperAnalytics(
                                fileref,
                                'Ended',
                                data.seconds,
                                data.duration,
                                Date.now()
                            )
                        }

                        let pausePlayers = () => {
                            $('iframe[data-youtube-player-guid]').each((index, videoPlayer) => {
                                if ($(videoPlayer).attr('data-youtube-player-guid') !== guid) {
                                    let otherPlayer = $(videoPlayer).data('youtube_player')
                                    if (
                                        otherPlayer &&
                                        otherPlayer.getPlayerState() === YT.PlayerState.PLAYING
                                    ) {
                                        otherPlayer.pauseVideo()
                                    }
                                }
                            })
                        }
                        let player = new YT.Player(divID, {
                            width: div.attr('data-width') || 400,
                            height: div.attr('data-height') || 250,
                            videoId: div.attr('data-youtube-id'),
                            playerVars: {
                                autoplay: 0,
                                controls: 1,
                                allowFullScreen: 1,
                            },
                            events: {
                                onStateChange: onPlayerStateChange,
                            },
                        })
                        $(this).data('youtube_player', player)
                    })
            }, 1000)
        }
    }
    /**
      Helper method that adds caliper media analytics to be sent to the backend
    */
    recordCaliperAnalytics(fileref, action, positionTime, duration, time, args = []) {
        let state = store.getState()
        let caliperStatsList = state.analyticsData.caliper[fileref] || []

        caliperStatsList.push({
            actionTaken: action,
            positionTime: positionTime,
            videoDuration: duration,
            eventTime: time,
            args: args,
        })

        store.dispatch(addCaliperData(fileref, caliperStatsList))
        return caliperStatsList[caliperStatsList.length - 1]
    }

    /**
      Helper method that updates video player statistics for the current player
    */
    recordVideoPlayerTime(fileref, newRecord, currentSeconds, totalSeconds) {
        let state = store.getState()
        let now = new Date().getTime()
        let stats = this.getVideoPlayerStats(fileref, newRecord)

        if (stats.play === 0) {
            stats.play = now
        }

        stats.pause = now

        if (totalSeconds > 0) {
            stats.total_blocks = Math.ceil(
                totalSeconds / state.VIDEO_BLOCK_IN_SECONDS
            )
        }

        if (currentSeconds >= 0) {
            let currentBlock = Math.floor(
                currentSeconds / state.VIDEO_BLOCK_IN_SECONDS
            )
            stats.blocks[currentBlock] = now
        }

        if (window.updateStatusButton) {
            window.updateStatusButton()
        }
    }

    /**
      Helper method that retrieves video player statistics for a given fileref
    */
    getVideoPlayerStats(fileref, newRecord) {
        let state = store.getState()
        let statsList = state.analyticsData.videos[fileref] || []
        if (newRecord || statsList.length === 0) {
            statsList.push({
                play: 0,
                pause: 0,
                blocks: {},
                total_blocks: 0,
            })
        }

        store.dispatch(updateVideoAnalyticsData(fileref, statsList))
        return statsList[statsList.length - 1]
    }
}
